import { useEffect, useState } from 'react';
import { content } from '@neopolis/content';

import { AccueilEcran } from './components/AccueilEcran.js';
import { PartieEcran } from './components/PartieEcran.js';
import { MentionsEtDonnees } from './components/MentionsEtDonnees.js';
import { EcranProf } from './prof/EcranProf.js';
import { createClientProf } from './prof/api-prof.js';
import { etapeMaximale, useGameStore } from './store/game-store.js';

/** L'adresse de l'espace enseignant. */
export const CHEMIN_PROF = '/prof';

/** L'adresse des mentions, accessible depuis l'accueil. */
export const CHEMIN_MENTIONS = '/mentions';

const clientProf = createClientProf(import.meta.env['VITE_API_URL'] ?? '');

/**
 * Aiguillage entre le jeu et l'espace enseignant.
 *
 * Deux adresses seulement — « / » et « /prof » — donc pas de routeur : une
 * lecture du chemin suffit, et le bundle ne grossit pas d'une bibliothèque
 * pour deux branches. Le retour en arrière du navigateur est géré par
 * « popstate ».
 */
export function App() {
  const [chemin, setChemin] = useState(() =>
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );

  useEffect(() => {
    const surRetour = (): void => setChemin(window.location.pathname);
    window.addEventListener('popstate', surRetour);
    return () => window.removeEventListener('popstate', surRetour);
  }, []);

  const naviguer = (vers: string): void => {
    window.history.pushState(null, '', vers);
    setChemin(vers);
  };

  const ecran = useGameStore((etat) => etat.ecran);
  const partie = useGameStore((etat) => etat.partie);
  const ilotReprenable = useGameStore((etat) => etat.ilotReprenable);
  const texteAgrandi = useGameStore((etat) => etat.texteAgrandi);
  const erreur = useGameStore((etat) => etat.erreur);
  const verdicts = useGameStore((etat) => etat.verdicts);
  const rapportOuvert = useGameStore((etat) => etat.rapportOuvert);
  const messagesEvenement = useGameStore((etat) => etat.messagesEvenement);
  const debatClasse = useGameStore((etat) => etat.debatClasse);
  const synchronisation = useGameStore((etat) => etat.synchronisation);
  const chapitresOuverts = useGameStore((etat) => etat.chapitresOuverts);
  const initialiser = useGameStore((etat) => etat.initialiser);
  const commencer = useGameStore((etat) => etat.commencer);
  const reprendre = useGameStore((etat) => etat.reprendre);
  const effacerErreur = useGameStore((etat) => etat.effacerErreur);
  const quitter = useGameStore((etat) => etat.quitter);
  const allerEtape = useGameStore((etat) => etat.allerEtape);
  const allerChapitre = useGameStore((etat) => etat.allerChapitre);
  const decouvrir = useGameStore((etat) => etat.decouvrir);
  const classer = useGameStore((etat) => etat.classer);
  const validerClassement = useGameStore((etat) => etat.validerClassement);
  const analyser = useGameStore((etat) => etat.analyser);
  const definirPriorites = useGameStore((etat) => etat.definirPriorites);
  const ecrireTrace = useGameStore((etat) => etat.ecrireTrace);
  const ouvrirRapport = useGameStore((etat) => etat.ouvrirRapport);
  const fermerRapport = useGameStore((etat) => etat.fermerRapport);
  const repondreAuQuestionnaire = useGameStore((etat) => etat.repondreAuQuestionnaire);
  const repondreQuestionGuidee = useGameStore((etat) => etat.repondreQuestionGuidee);
  const reglerProgramme = useGameStore((etat) => etat.reglerProgramme);
  const organiserTournee = useGameStore((etat) => etat.organiserTournee);
  const poserEquipement = useGameStore((etat) => etat.poserEquipement);
  const retirerEquipement = useGameStore((etat) => etat.retirerEquipement);
  const deployer = useGameStore((etat) => etat.deployer);
  const resoudreEvenement = useGameStore((etat) => etat.resoudreEvenement);
  const revenirAConcevoir = useGameStore((etat) => etat.revenirAConcevoir);
  const basculerTexteAgrandi = useGameStore((etat) => etat.basculerTexteAgrandi);

  useEffect(() => {
    void initialiser();
  }, [initialiser]);

  useEffect(() => {
    document.documentElement.classList.toggle('texte-agrandi', texteAgrandi);
  }, [texteAgrandi]);

  if (chemin.startsWith(CHEMIN_MENTIONS)) {
    return <MentionsEtDonnees content={content} onRetour={() => naviguer('/')} />;
  }

  if (chemin.startsWith(CHEMIN_PROF)) {
    return <EcranProf content={content} client={clientProf} onRetourJeu={() => naviguer('/')} />;
  }

  if (ecran === 'partie' && partie) {
    return (
      <PartieEcran
        content={content}
        partie={partie}
        etapeMaximale={etapeMaximale(partie)}
        texteAgrandi={texteAgrandi}
        verdicts={verdicts}
        rapportOuvert={rapportOuvert}
        messagesEvenement={messagesEvenement}
        debatClasse={debatClasse}
        erreur={erreur}
        synchronisation={synchronisation}
        chapitresOuverts={chapitresOuverts}
        actions={{
          onAllerEtape: allerEtape,
          onDecouvrir: decouvrir,
          onClasser: classer,
          onValiderClassement: validerClassement,
          onAnalyser: analyser,
          onDefinirPriorites: definirPriorites,
          onEcrireTrace: ecrireTrace,
          onReglerProgramme: reglerProgramme,
          onOrganiserTournee: organiserTournee,
          onOuvrirRapport: ouvrirRapport,
          onFermerRapport: fermerRapport,
          onRepondreQuestionGuidee: repondreQuestionGuidee,
          onPoserEquipement: poserEquipement,
          onRetirerEquipement: retirerEquipement,
          onDeployer: deployer,
          onResoudreEvenement: resoudreEvenement,
          onRevenirAConcevoir: revenirAConcevoir,
          onChapitreSuivant: () => allerChapitre('ch2'),
          onRepondreAuQuestionnaire: repondreAuQuestionnaire,
          onBasculerTexte: basculerTexteAgrandi,
          onQuitter: quitter,
        }}
      />
    );
  }

  return (
    <AccueilEcran
      content={content}
      ilotReprenable={ilotReprenable}
      erreur={erreur}
      onCommencer={(code) => void commencer(code)}
      onReprendre={() => void reprendre()}
      onMentions={() => naviguer(CHEMIN_MENTIONS)}
      onEffacerErreur={effacerErreur}
    />
  );
}
